import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MoveElementEvent } from '@modules/elements/directives/move-element.directive';
import { PreviewService } from '@modules/preview/services/preview.service';
import { Clip } from '@modules/preview/types/clip';
import { Timecode, TimecodeNull } from '@modules/preview/types/timecode';
import { Record } from '@modules/records/types/record';
import { BehaviorSubject, distinctUntilChanged, filter, map, startWith, Subject, switchMap, takeUntil, throttleTime } from 'rxjs';

@Component({
    selector: 'app-playback-progress-bar',
    templateUrl: './playback-progress-bar.component.html',
    styleUrl: './playback-progress-bar.component.less',
    standalone: false
})
export class PlaybackProgressBarComponent implements OnInit, OnChanges, OnDestroy {

  // ViewChild
  @ViewChild('progress', { static: false }) progressRef?: ElementRef;
  @ViewChild('currentPosition', { static: false }) currentPositionRef?: ElementRef;
  @ViewChild('hoverPosition', { static: false }) hoverPositionRef?: ElementRef;
  @ViewChild('hoverTC', { static: false }) hoverTCRef?: ElementRef;
  @ViewChild('inPointPosition', { static: false }) inPointPositionRef?: ElementRef;
  @ViewChild('outPointPosition', { static: false }) outPointPositionRef?: ElementRef;
  @ViewChild('clipProgress', { static: false }) clipProgressRef?: ElementRef;
  @ViewChild('inPointOvertimePosition', { static: false }) inPointOvertimePositionRef?: ElementRef;
  @ViewChild('outPointOvertimePosition', { static: false }) outPointOvertimePositionRef?: ElementRef;
  @ViewChild('clipOvertimeProgress', { static: false }) clipOvertimeProgressRef?: ElementRef;

  // Inputs
  @Input() clip?: Clip;
  @Input() record?: Record;
  @Input() serverId?: string;

  // Output
  @Output() jump = new EventEmitter<{tc?: string, frame?: number, offset?: number}>();

  // Public
  public currentFrame: number = 0;
  public duration: number = 1;
  public showHoverPosition = false;
  public hoverCurrentPosition = false;
  public dragCurrentPosition = false;
  public dragFromFrame: number = 0;
  public inPoint?: string;
  public outPoint?: string;
  public inPointOvertime?: string;
  public outPointOvertime?: string;

  // Private
  private alive = new Subject<void>();
  private jumpToFrame = new Subject<number>();

  /**
   * Constructor
   */

  constructor(
    private previewService: PreviewService,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    // Handle Jump to Frame
    this.jumpToFrame
      .pipe(
        throttleTime(100),
        takeUntil(this.alive)
      )
      .subscribe(frame => {
        this.jump.emit({ frame });
        if (this.record?.id && this.serverId) {
          this.previewService.jump(this.record?.id, [this.serverId], {frame});
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('clip' in changes) {
      this.duration = this.clip?.durationFrames ?? 1;
      console.log('[CLIP] ', this.clip);
      setTimeout(() => this.updateInOutPoints(), 100);
    }
    if ('record' in changes || 'serverId' in changes) {
      let trimPoints: {start?: string, end?: string} = {};
      let overtimeTrimPoints: {start?: string, end?: string} = {};
      if (this.record && this.serverId) {
        trimPoints = Record.getTrimPoints(this.record, this.serverId, false);
        overtimeTrimPoints = Record.getTrimPoints(this.record, this.serverId, true);
      }
      this.inPoint = trimPoints?.start;
      this.outPoint = trimPoints?.end;
      this.inPointOvertime = overtimeTrimPoints?.start;
      this.outPointOvertime = overtimeTrimPoints?.end;
      this.updateInOutPoints();
      this.updateInOutOvertimePoints();
    }
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  setValue(value: number | string): void {
    if (value !== null && value !== undefined && this.clip) {
      try {
        const timecode = new Timecode(value, this.clip.timebase!, !!this.clip.drop);
        this.currentFrame = timecode.frameCount - (this.clip.startTimecodeFrames || 0);
        if (this.currentPositionRef && !this.dragCurrentPosition) {
          this.currentPositionRef.nativeElement.style.left = ((this.currentFrame / this.duration) * 100) + '%';
        }
      } catch (error) {
        console.error('[ProgressBar] ', error)
      }
    }
  }

  getPositionAndTimecode(event: MouseEvent): ({x: number, timecode: Timecode, frame: number}) | any {
    const rect = this.progressRef?.nativeElement.getBoundingClientRect();
    const x = Math.min(event.clientX - rect.left, rect.width);
    const persent = x / rect.width;
    let frames = Math.floor(this.duration * persent);
    frames = Math.min(Math.max(frames, 0), ((this.clip?.durationFrames || 1) - 1));
    const timecode = new Timecode((this.clip?.startTimecode || 0), (this.clip?.timebase || 24), !!this.clip?.drop);
    timecode.add(frames);
    return {x, timecode, frame: frames};
  }

  updateInOutPoints(): void {
    // In Point
    if (this.inPoint !== undefined && this.inPointPositionRef && this.clip) {
      const inPoint = new Timecode(this.inPoint, this.clip.timebase!, !!this.clip.drop).frameCount;
      const inPointCurrentFrame = inPoint - (this.clip.startTimecodeFrames || 0);
      this.inPointPositionRef.nativeElement.style.left = ((inPointCurrentFrame / this.duration) * 100) + '%';
    }
    // Out Point
    if (this.outPoint !== undefined && this.outPointPositionRef && this.clip) {
      const outPoint = new Timecode(this.outPoint, this.clip.timebase!, !!this.clip.drop).frameCount;
      const outPointCurrentFrame = outPoint - (this.clip.startTimecodeFrames || 0);
      this.outPointPositionRef.nativeElement.style.left = ((outPointCurrentFrame / this.duration) * 100) + '%';
    }
    if (this.clipProgressRef) {
      if (this.clip && 'startTimecodeFrames' in this.clip && 'endTimecodeFrames' in this.clip) {
        const inPoint = this.inPoint !== undefined ? new Timecode(this.inPoint, this.clip.timebase!, !!this.clip.drop).frameCount : 0;
        const outPoint = this.outPoint !== undefined ? new Timecode(this.outPoint, this.clip.timebase!, !!this.clip.drop).frameCount : 0;
        const inPointCurrentFrame = Math.max(inPoint - (this.clip.startTimecodeFrames || 0), 0);
        const clipFramesSize = (outPoint || (this.clip.endTimecodeFrames || 0)) - (inPoint || (this.clip.startTimecodeFrames || 0));
        this.clipProgressRef.nativeElement.style.left = ((inPointCurrentFrame / this.duration) * 100) + '%';
        this.clipProgressRef.nativeElement.style.width = ((clipFramesSize / this.duration) * 100) + '%';
      } else {
        this.clipProgressRef.nativeElement.style.left = 0;
        this.clipProgressRef.nativeElement.style.width = 0;
      }
    }
  }

  updateInOutOvertimePoints(): void {
    // In Point
    if (this.inPointOvertime !== undefined && this.inPointOvertimePositionRef && this.clip) {
      const inPointOvertime = new Timecode(this.inPointOvertime, this.clip.timebase!, !!this.clip.drop).frameCount;
      const inPointCurrentFrame = inPointOvertime - (this.clip.startTimecodeFrames || 0);
      this.inPointOvertimePositionRef.nativeElement.style.left = ((inPointCurrentFrame / this.duration) * 100) + '%';
    }
    // Out Point
    if (this.outPointOvertime !== undefined && this.outPointOvertimePositionRef && this.clip) {
      const outPointOvertime = new Timecode(this.outPointOvertime, this.clip.timebase!, !!this.clip.drop).frameCount;
      const outPointCurrentFrame = outPointOvertime - (this.clip.startTimecodeFrames || 0);
      this.outPointOvertimePositionRef.nativeElement.style.left = ((outPointCurrentFrame / this.duration) * 100) + '%';
    }
    if (this.clipOvertimeProgressRef) {
      if (this.clip && 'startTimecodeFrames' in this.clip && 'endTimecodeFrames' in this.clip) {
        const inPointOvertime = this.inPointOvertime !== undefined ? new Timecode(this.inPointOvertime, this.clip.timebase!, !!this.clip.drop).frameCount : 0;
        const outPointOvertime = this.outPointOvertime !== undefined ? new Timecode(this.outPointOvertime, this.clip.timebase!, !!this.clip.drop).frameCount : 0;
        const inPointCurrentFrame = Math.max(inPointOvertime - (this.clip.startTimecodeFrames || 0), 0);
        const clipFramesSize = (outPointOvertime || (this.clip.endTimecodeFrames || 0)) - (inPointOvertime || (this.clip.startTimecodeFrames || 0));
        this.clipOvertimeProgressRef.nativeElement.style.left = ((inPointCurrentFrame / this.duration) * 100) + '%';
        this.clipOvertimeProgressRef.nativeElement.style.width = ((clipFramesSize / this.duration) * 100) + '%';
      } else {
        this.clipOvertimeProgressRef.nativeElement.style.left = 0;
        this.clipOvertimeProgressRef.nativeElement.style.width = 0;
      }
    }
  }

  /**
   * Actions
   */

  selectProgress(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const { frame } = this.getPositionAndTimecode(event);
    this.jump.emit({ frame });
    if (this.record?.id && this.serverId) {
      this.previewService.jump(this.record.id, [this.serverId], {frame});
    }
  }

  hoverProgress(event: any): void {
    const { x, timecode } = this.getPositionAndTimecode(event);
    if (this.hoverPositionRef && x > 0) {
      this.hoverPositionRef.nativeElement.style.left = x + 'px';
    }
    if (this.hoverTCRef) {
      this.hoverTCRef.nativeElement.style.left = x + 'px';
      this.hoverTCRef.nativeElement.innerHTML = timecode.toString() ?? '00:00:00:00';
    }
  }

  /**
   * Drag and drop Playback position
   */

  dragStartPlaybackPosition(event: MoveElementEvent | any): void {
    this.dragCurrentPosition = true;
    this.dragFromFrame = this.currentFrame;
  }

  dragMovePlaybackPosition(event: MoveElementEvent | any): void {
    if (!this.clip?.startTimecode || !this.clip?.timebase) {
      return;
    }
    const diffPersent = event.difference.x / this.progressRef?.nativeElement.offsetWidth;
    const diffFrame = Math.floor((this.duration - 1) * diffPersent);
    const frame = Math.max(0, Math.min(this.duration - 1, this.dragFromFrame + diffFrame));
    
    const timecode = new Timecode((this.clip.startTimecode), this.clip?.timebase, !!this.clip?.drop);
    timecode.add(frame);
    if (this.currentPositionRef) {
      this.currentPositionRef.nativeElement.style.left = (frame / (this.duration - 1)) * 100 + '%';
    }
    if (this.hoverTCRef) {
      this.hoverTCRef.nativeElement.style.left = (frame / (this.duration - 1)) * 100 + '%';
      this.hoverTCRef.nativeElement.innerHTML = timecode.toString() ?? '00:00:00:00';
    }
    this.jumpToFrame.next(frame);
  }

  dragEndPlaybackPosition(event: MoveElementEvent | any): void {
    this.dragCurrentPosition = false;
  }

  jumpTo(frame: number): void {
    if (frame >= 0 && frame < (this.clip?.durationFrames || 0)) {
      this.jump.emit({ frame });
      if (this.record?.id && this.serverId) {
        this.previewService.jump(this.record.id, [this.serverId], {frame});
      }
    }
  }

}
